import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Save, Send } from 'lucide-react';
import debtorApi from '../../api/debtorApi';
import creditSubmissionApi, { CreditSubmission } from '../../api/creditSubmissionApi';
import { useToast } from '../../context/ToastContext';

// Step Components
import StepProgress from '../../components/credit/submission/StepProgress';
import Step1GeneralInfo from '../../components/credit/submission/Step1GeneralInfo';
import Step2LoanNeed from '../../components/credit/submission/Step2LoanNeed';
import Step3DebtorData from '../../components/credit/submission/Step3DebtorData';
import Step4BankHistory from '../../components/credit/submission/Step4BankHistory';
import Step5SLIK from '../../components/credit/submission/Step5SLIK';
import Step6Address from '../../components/credit/submission/Step6Address';
import Step7Relations from '../../components/credit/submission/Step7Relations';
import Step8BusinessDocs from '../../components/credit/submission/Step8BusinessDocs';
import Step9Collateral from '../../components/credit/submission/Step9Collateral';
import Step10CreditHistory from '../../components/credit/submission/Step10CreditHistory';
import CashFlowProjection from '../../components/credit/submission/CashFlowProjection';

const STEPS = [
  'Informasi Umum',
  'Kebutuhan Pinjaman',
  'Data Pemohon',
  'Riwayat Bank',
  'Ringkasan SLIK',
  'Alamat & Lokasi',
  'Relasi Debitur',
  'Dokumen Usaha',
  'Agunan',
  'Riwayat Kredit',
  'Proyeksi Keuangan',
];

const STORAGE_KEY = 'credit_submission_draft';

const initialFormData = {
  submission_number: '',
  status: 'draft',
  debtor_id: '',
  application_type: '',
  credit_system: '',
  debtor_type: 'individual',
  is_entrepreneur: false,
  debtor_profile: '',
  loan_purpose: '',
  requested_amount: '',
  debtor_name: '',
  id_number: '',
  birth_place: '',
  birth_date: '',
  gender: '',
  marital_status: '',
  education: '',
  photo_url: '',
  phone: '',
  mother_maiden_name: '',
  emergency_contact: '',
  bank_relationship: '',
  last_facility: '',
  last_credit_quality: '',
  last_arrears: '',
  slik_document_url: '',
  slik_effective_plafon: '',
  slik_outstanding: '',
  slik_worst_quality: '',
  slik_data_month: '',
  slik_position_date: '',
  address: '',
  village: '',
  district: '',
  city: '',
  latitude: null as number | null,
  longitude: null as number | null,
  relations: [] as any[],
  business_documents: [] as any[],
  collateral_info: {} as any,
  collateral_documents: [] as any[],
  collateral_latitude: null as number | null,
  collateral_longitude: null as number | null,
  appraisal_data: {} as any,
  credit_history: [] as any[],
  financial_projection: {} as any,
  current_step: 1,
  scoring_model_id: '',
};

export default function CreditSubmissionForm() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const urlParams = new URLSearchParams(window.location.search);
  const submissionId = urlParams.get('id');
  const isEdit = !!submissionId;
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [debtors, setDebtors] = useState<any[]>([]);
  const [scoringModels, setScoringModels] = useState<any[]>([]);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const dbtrs = await debtorApi.getAll();
      setDebtors(dbtrs);

      // TODO: Load scoring models and existing submission if editing
      setScoringModels([
        { id: '1', credit_type: 'working_capital', title: 'Modal Kerja' },
        { id: '2', credit_type: 'investment', title: 'Investasi' },
        { id: '3', credit_type: 'consumer', title: 'Konsumtif' },
      ]);

      if (!isEdit) {
        // Only generate new submission number if not loaded from draft
        const savedDraft = localStorage.getItem(STORAGE_KEY);
        if (!savedDraft) {
          const timestamp = Date.now().toString().slice(-8);
          setFormData(prev => ({ ...prev, submission_number: `SUB${timestamp}` }));
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [isEdit]);

  // Load draft from localStorage on mount
  useEffect(() => {
    if (!isEdit) {
      const savedDraft = localStorage.getItem(STORAGE_KEY);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          setFormData(draft.formData || initialFormData);
          setCurrentStep(draft.currentStep || 1);
        } catch (error) {
          console.error('Error loading draft:', error);
        }
      }
    }
    loadInitialData();
  }, [isEdit, loadInitialData]);

  // Auto-save to localStorage whenever formData or currentStep changes (with debounce)
  // This is a backup, but users can also save manually per step
  useEffect(() => {
    if (!isEdit) {
      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout to auto-save after 2 seconds of inactivity
      saveTimeoutRef.current = setTimeout(() => {
        const draft = {
          formData,
          currentStep,
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
      }, 2000);
    }

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData, currentStep, isEdit]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const checkStepValid = (step: number): boolean => {
    // Check validation without setting errors (for button disabled state)
    if (step === 1) {
      if (!formData.application_type || formData.application_type.trim() === '') {
        return false;
      }
      if (!formData.debtor_type) {
        return false;
      }
    }

    if (step === 2) {
      const amount = formData.requested_amount;
      if (!amount || amount === '' || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
        return false;
      }
    }

    if (step === 3) {
      if (!formData.debtor_name || formData.debtor_name.trim() === '') {
        return false;
      }
      if (!formData.id_number || formData.id_number.trim() === '') {
        return false;
      }
    }

    if (step === 4) {
      if (!formData.bank_relationship || formData.bank_relationship.trim() === '') {
        return false;
      }
      if (!formData.last_credit_quality) {
        return false;
      }
    }

    return true;
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.application_type || formData.application_type.trim() === '') {
        newErrors.application_type = 'Jenis permohonan harus dipilih';
      }
      if (!formData.debtor_type) {
        newErrors.debtor_type = 'Jenis debitur harus dipilih';
      }
    }

    if (step === 2) {
      const amount = formData.requested_amount;
      if (!amount || amount === '' || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
        newErrors.requested_amount = 'Nominal dana yang dibutuhkan harus diisi';
      }
    }

    if (step === 3) {
      if (!formData.debtor_name || formData.debtor_name.trim() === '') {
        newErrors.debtor_name = 'Nama debitur harus diisi';
      }
      if (!formData.id_number || formData.id_number.trim() === '') {
        newErrors.id_number = 'Nomor KTP harus diisi';
      }
    }

    if (step === 4) {
      if (!formData.bank_relationship || formData.bank_relationship.trim() === '') {
        newErrors.bank_relationship = 'Hubungan dengan bank harus diisi';
      }
      if (!formData.last_credit_quality) {
        newErrors.last_credit_quality = 'Kualitas kredit terakhir harus dipilih';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isStepValid = (): boolean => {
    return checkStepValid(currentStep);
  };

  const handleDebtorSearch = (idNumber: string) => {
    if (!idNumber) return;
    const debtor = debtors.find(d => d.id_number === idNumber);
    if (debtor) {
      setFormData(prev => ({
        ...prev,
        debtor_id: debtor.id,
        debtor_name: debtor.full_name,
        id_number: debtor.id_number,
        birth_place: debtor.birth_place || '',
        birth_date: debtor.birth_date?.split('T')[0] || '',
        gender: debtor.gender || '',
        marital_status: debtor.marital_status || '',
        phone: debtor.phone || '',
        address: debtor.address || '',
        city: debtor.city || '',
        latitude: debtor.latitude,
        longitude: debtor.longitude,
      }));
    }
  };

  // Relations handlers
  const addRelation = () => {
    setFormData(prev => ({
      ...prev,
      relations: [
        ...prev.relations,
        { type: 'individual', name: '', birth_info: '', relation: '', photo_url: '' },
      ],
    }));
  };

  const updateRelation = (index: number, field: string, value: string) => {
    const relations = [...formData.relations];
    relations[index][field] = value;
    setFormData(prev => ({ ...prev, relations }));
  };

  const removeRelation = (index: number) => {
    setFormData(prev => ({
      ...prev,
      relations: prev.relations.filter((_, i) => i !== index),
    }));
  };

  // Business documents handlers
  const addBusinessDocument = () => {
    setFormData(prev => ({
      ...prev,
      business_documents: [...prev.business_documents, { file_url: '', title: '', notes: '' }],
    }));
  };

  const updateBusinessDocument = (index: number, field: string, value: string) => {
    const docs = [...formData.business_documents];
    docs[index][field] = value;
    setFormData(prev => ({ ...prev, business_documents: docs }));
  };

  const removeBusinessDocument = (index: number) => {
    setFormData(prev => ({
      ...prev,
      business_documents: prev.business_documents.filter((_, i) => i !== index),
    }));
  };

  // Collateral documents handlers
  const addCollateralDocument = () => {
    setFormData(prev => ({
      ...prev,
      collateral_documents: [...prev.collateral_documents, { file_url: '', title: '', notes: '' }],
    }));
  };

  const updateCollateralDocument = (index: number, field: string, value: string) => {
    const docs = [...formData.collateral_documents];
    docs[index][field] = value;
    setFormData(prev => ({ ...prev, collateral_documents: docs }));
  };

  const removeCollateralDocument = (index: number) => {
    setFormData(prev => ({
      ...prev,
      collateral_documents: prev.collateral_documents.filter((_, i) => i !== index),
    }));
  };

  const handleSaveStep = () => {
    // Validasi step saat ini
    if (!validateStep(currentStep)) {
      showError('Mohon lengkapi field yang wajib diisi terlebih dahulu');
      return;
    }

    // Simpan step saat ini ke localStorage
    const draft = {
      formData,
      currentStep,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    showSuccess(`Step ${currentStep} (${STEPS[currentStep - 1]}) berhasil disimpan`);
  };

  const handleSave = async (shouldSubmit = false) => {
    // Validasi step saat ini sebelum save
    if (!shouldSubmit && !validateStep(currentStep)) {
      showError('Mohon lengkapi field yang wajib diisi terlebih dahulu');
      return;
    }

    // Untuk submit, validasi semua step yang diperlukan
    if (shouldSubmit) {
      // Validasi semua step penting
      for (let i = 1; i <= currentStep; i++) {
        if (!validateStep(i)) {
          showError(`Mohon lengkapi data pada step ${i} (${STEPS[i - 1]})`);
          return;
        }
      }
    }

    setSaving(true);
    try {
      const status: 'draft' | 'submitted' = shouldSubmit ? 'submitted' : 'draft';

      // Convert string fields to numbers where needed
      const parseNumber = (value: any): number | null => {
        if (!value && value !== 0) return null;
        const num =
          typeof value === 'string' ? parseFloat(value.replace(/[^\d]/g, '')) : Number(value);
        return isNaN(num) ? null : num;
      };

      const submissionData: Partial<CreditSubmission> = {
        ...formData,
        status,
        debtor_type: (formData.debtor_type || 'individual') as 'individual' | 'corporate',
        debtor_id: formData.debtor_id ? parseInt(formData.debtor_id.toString()) : null,
        scoring_model_id: formData.scoring_model_id
          ? parseInt(formData.scoring_model_id.toString())
          : null,
        requested_amount: parseNumber(formData.requested_amount) || null,
        last_arrears: parseNumber(formData.last_arrears) || null,
        slik_effective_plafon: parseNumber(formData.slik_effective_plafon) || null,
        slik_outstanding: parseNumber(formData.slik_outstanding) || null,
      };

      // Ensure required fields are present
      if (!submissionData.debtor_name || !submissionData.id_number) {
        showError('Nama debitur dan Nomor KTP harus diisi');
        setSaving(false);
        return;
      }

      if (isEdit && submissionId) {
        // Update existing submission
        await creditSubmissionApi.update(parseInt(submissionId), submissionData);
        showSuccess(shouldSubmit ? 'Pengajuan kredit berhasil dikirim' : 'Draft berhasil disimpan');
      } else {
        // Create new submission
        await creditSubmissionApi.create(submissionData);
        showSuccess(shouldSubmit ? 'Pengajuan kredit berhasil dikirim' : 'Draft berhasil disimpan');
      }

      // Clear localStorage after successful save/submit
      if (shouldSubmit) {
        localStorage.removeItem(STORAGE_KEY);
        // Navigate immediately, data will reload in ApprovalList
        navigate('/credit/approval');
      } else {
        // Save all steps to localStorage
        const draft = {
          formData,
          currentStep,
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
      }
    } catch (error: any) {
      console.error('Error saving:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response,
        data: error?.response?.data,
      });
      const errorMessage =
        error?.response?.data?.error || error?.message || 'Gagal menyimpan pengajuan kredit';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Function to clear draft (can be called when needed)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const clearDraft = () => {
    localStorage.removeItem(STORAGE_KEY);
    setFormData(initialFormData);
    setCurrentStep(1);
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      // Auto-save current step before moving to next
      const draft = {
        formData,
        currentStep,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));

      if (currentStep < STEPS.length) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleStepClick = (step: number) => {
    // Allow navigation to any step (completed or future)
    // If moving forward, validate current step first
    if (step > currentStep) {
      if (validateStep(currentStep)) {
        setCurrentStep(step);
        window.scrollTo(0, 0);
      }
    } else {
      // Moving backward or to completed step - allow without validation
      setCurrentStep(step);
      window.scrollTo(0, 0);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Pengajuan Kredit' : 'Pengajuan Kredit Baru'}
          </h1>
          <p className="text-gray-500 mt-1">{formData.submission_number}</p>
        </div>
        <span className="px-4 py-2 border rounded-lg text-lg font-bold">
          Step {currentStep} / {STEPS.length}
        </span>
      </div>

      {/* Progress */}
      <StepProgress currentStep={currentStep} steps={STEPS} onStepClick={handleStepClick} />

      {/* Form Content */}
      <div className="bg-white border rounded-lg">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">{STEPS[currentStep - 1]}</h2>
          <p className="text-gray-500 text-sm mt-1">Lengkapi informasi pada step ini</p>
        </div>
        <div className="p-6 animate-fadeIn" key={currentStep}>
          {currentStep === 1 && (
            <Step1GeneralInfo
              formData={formData}
              handleChange={handleChange}
              scoringModels={scoringModels}
              errors={errors}
            />
          )}
          {currentStep === 2 && (
            <Step2LoanNeed formData={formData} handleChange={handleChange} errors={errors} />
          )}
          {currentStep === 3 && (
            <Step3DebtorData
              formData={formData}
              handleChange={handleChange}
              onDebtorSearch={handleDebtorSearch}
              errors={errors}
            />
          )}
          {currentStep === 4 && (
            <Step4BankHistory formData={formData} handleChange={handleChange} errors={errors} />
          )}
          {currentStep === 5 && <Step5SLIK formData={formData} handleChange={handleChange} />}
          {currentStep === 6 && <Step6Address formData={formData} handleChange={handleChange} />}
          {currentStep === 7 && (
            <Step7Relations
              relations={formData.relations}
              onAdd={addRelation}
              onUpdate={updateRelation}
              onRemove={removeRelation}
            />
          )}
          {currentStep === 8 && (
            <Step8BusinessDocs
              documents={formData.business_documents}
              onAdd={addBusinessDocument}
              onUpdate={updateBusinessDocument}
              onRemove={removeBusinessDocument}
            />
          )}
          {currentStep === 9 && (
            <Step9Collateral
              formData={formData}
              handleChange={handleChange}
              collateralDocuments={formData.collateral_documents}
              onAddCollateralDoc={addCollateralDocument}
              onUpdateCollateralDoc={updateCollateralDocument}
              onRemoveCollateralDoc={removeCollateralDocument}
            />
          )}
          {currentStep === 10 && <Step10CreditHistory creditHistory={formData.credit_history} />}
          {currentStep === 11 && (
            <CashFlowProjection
              data={formData.financial_projection}
              onChange={data => handleChange('financial_projection', data)}
            />
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Sebelumnya
          </button>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSaveStep}
              disabled={!isStepValid()}
              className="px-4 py-2 border rounded-lg hover:bg-gray-100 hover:shadow-md active:scale-95 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              Simpan Draft
            </button>
            {currentStep < STEPS.length ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={!isStepValid()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-300 active:scale-95 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Selanjutnya
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleSave(true)}
                disabled={saving || !isStepValid()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 hover:shadow-lg hover:shadow-green-300 active:scale-95 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {saving ? 'Mengirim...' : 'Kirim Pengajuan'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
